import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Dropdown, Input } from "antd";
import { Button } from "@/shared/components/button";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import type { PatientProfile } from "@/portals/patient/api/patientProfileApi";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import { NotificationsMenu } from "@/shared/layout/NotificationsMenu";
import { ConnectDataButton } from "@/features/patient/epic-connect/components/ConnectDataButton";

interface PatientHeaderProps {
  profile: PatientProfile;
  titleBlack?: string;
  titleRed?: string;
  description?: string;
}

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export function PatientHeader({ profile, titleBlack, titleRed, description }: PatientHeaderProps) {
  const navigate = useNavigate();
  const signOut = usePatientAuthStore((s) => s.signOut);
  const [searchValue, setSearchValue] = useState("");
  const { avatarSrc } = usePatientProfile();

  const handleViewProfile = () => {
    navigate("/patient/profile");
  };

  const handleLogout = async () => {
    await signOut();
  };

  const menuContent = (
    <div className="w-[150px] overflow-hidden rounded-lg border border-[#DADDE5] bg-white shadow-[0_4px_12px_rgba(17,24,39,0.14)]">
      <Button
        variant="ghost"
        onClick={handleViewProfile}
        className="!block !h-auto !w-full !justify-start !rounded-none !px-4 !py-3 !text-left !text-sm !font-medium !text-[#180047] hover:!bg-[#F7F7F7] hover:!text-[#180047]"
      >
        View Profile
      </Button>
      <div className="h-px bg-[#E5E7EB]" />
      <Button
        variant="ghost"
        onClick={handleLogout}
        className="!block !h-auto !w-full !justify-start !rounded-none !px-4 !py-3 !text-left !text-sm !font-medium !text-[#E4002B] hover:!bg-[#FFF2F4] hover:!text-[#E4002B]"
      >
        Logout
      </Button>
    </div>
  );

  return (
    <header className="flex h-[73px] w-full shrink-0 items-center justify-between border-b border-[#E5E7EB] bg-[#F7F7F7] px-6">

      {/* Left — page title + description OR welcome message */}
      <div className="flex flex-col justify-center gap-0">
        {titleBlack ? (
          <>
            <h2 className="text-lg tracking-tight text-[#111827]" style={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
              {titleBlack}
            </h2>
            {description && (
              <p className="text-[11px] leading-none text-[#9CA3AF]">{description}</p>
            )}
          </>
        ) : (
          <>
            <h2 className="text-lg tracking-tight text-[#111827]" style={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
              Welcome back, {profile.firstName}!
            </h2>
            <p className="text-xs leading-none text-[#9CA3AF]">
              Here's what's happening with your health this month
            </p>
          </>
        )}
      </div>

      {/* Right — Search + Bell + Avatar */}
      <div className="flex items-center gap-3">
        <Input
          prefix={<Icon icon="lucide:search" className="h-4 w-4 text-[#4B5563]" />}
          placeholder="Search anything..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="!h-8 !w-64 !rounded-full !border-black !bg-white !text-sm !text-[#4B5563] placeholder:!text-[#4B5563]"
        />
        <ConnectDataButton />
        <NotificationsMenu />
        <Dropdown trigger={["click"]} popupRender={() => menuContent} placement="bottomRight">
          <Button
            variant="ghost"
            aria-label="Open profile menu"
            className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !bg-[#180047] !p-0 !text-center !text-xs !font-bold !leading-none !text-white hover:!bg-[#180047] hover:!opacity-85"
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="h-full w-full rounded-full object-cover" />
            ) : (
              <span className="block leading-none !text-white">
                {getInitials(profile.firstName, profile.lastName)}
              </span>
            )}
          </Button>
        </Dropdown>
      </div>
    </header>
  );
}
